import React, { useLayoutEffect, useRef, useState } from 'react';

interface SiloIconProps {
    percent: number;
    warning?: boolean;
    warningMessage?: string;
    width?: number;
    height?: number;
}

const SiloIcon: React.FC<SiloIconProps> = ({ percent, warning, warningMessage, width = 120, height = 140 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const clipPathIdRef = useRef<string>('silo-fill-clip-' + Math.random().toString(36).slice(2, 9));

    const fillAreaTop = 16;
    const fillAreaBottom = 116;
    const fillAreaHeight = Math.max(0, fillAreaBottom - fillAreaTop);

    const clampedPercent = Math.min(100, Math.max(0, percent));
    const rawFillHeight = (clampedPercent / 100) * fillAreaHeight;
    const minVisibleHeight = clampedPercent > 0 && clampedPercent < 100 ? 4 : 0;
    const fillHeight = Math.max(rawFillHeight, minVisibleHeight);
    const fillY = fillAreaBottom - fillHeight;

    const svgMarginLeft = 0;
    const svgMarginTop = 20;

    const containerRef = useRef<HTMLDivElement>(null);
    const warnGroupRef = useRef<SVGGElement>(null);
    const [overlayRect, setOverlayRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

    useLayoutEffect(() => {
        const updateOverlay = (): void => {
            if (!containerRef.current || !warnGroupRef.current) return;
            const containerBox = containerRef.current.getBoundingClientRect();
            const warnBox = warnGroupRef.current.getBoundingClientRect();
            const padding = 6;
            const rightNudge = 4;
            setOverlayRect({
                left: warnBox.left - containerBox.left - padding + rightNudge,
                top: warnBox.top - containerBox.top - padding,
                width: warnBox.width + padding * 2,
                height: warnBox.height + padding * 2,
            });
        };
        updateOverlay();
        const onMove = (): void => updateOverlay();
        window.addEventListener('resize', updateOverlay);
        const container = containerRef.current;
        container?.addEventListener('mouseenter', updateOverlay);
        container?.addEventListener('mousemove', onMove);
        return () => {
            window.removeEventListener('resize', updateOverlay);
            container?.removeEventListener('mouseenter', updateOverlay);
            container?.removeEventListener('mousemove', onMove);
        };
    }, [width, height]);

    return (
        <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
            <svg viewBox="0 0 140 140" width={width} height={height} style={{ marginLeft: svgMarginLeft, marginTop: svgMarginTop }}>
                <path
                    d="M119.3,134h-16.9c-.8,0-1.5-.6-1.5-1.5V15.2c0-.7-.4-1.2-1-1.5L70.6.1c-.2,0-.4-.1-.6-.1s-.4,0-.6.1l-29.3,13.5c-.6.3-1,.9-1,1.5v117.4c0,.8-.7,1.5-1.5,1.5h-16.9c-.8,0-1.5.7-1.5,1.5v3c0,.8.7,1.5,1.5,1.5h98.5c.8,0,1.5-.7,1.5-1.5v-3c0-.8-.7-1.5-1.5-1.5ZM94.9,132.6c0,.8-.7,1.5-1.5,1.5h-46.9c-.8,0-1.5-.6-1.5-1.5v-30.8c0-1.1,1.2-1.9,2.2-1.3l14,8.2c.4.3.7.8.7,1.3v4.9c0,.8.7,1.5,1.5,1.5h13.1c.8,0,1.5-.6,1.5-1.5v-4.9c0-.5.3-1,.7-1.3l14-8.2c1-.6,2.2.1,2.2,1.3v30.8ZM98.8,93.8c0,.5-.2.9-.7,1.1l-8.4,5-11.2,6.6h-.2c0,.1,0,.1,0,.1h0l-1,.7c-.7.5-1.1,1.3-1.1,2.3v4.1c0,.2-.1.4-.3.4h-11.6c-.2,0-.3-.2-.3-.4v-4.1c0-.9-.4-1.8-1.1-2.3l-1.2-.8-1.4-.9-18.3-10.8c-.4-.2-.6-.7-.6-1.1V21.2h0v-4.5c0-.5.3-1,.8-1.3l27.4-12.7c.4-.2.8-.2,1.1,0l27.4,12.7c.5.2.8.7.8,1.3v67.1c0,0,0,.1,0,.2v9.9Z"
                    fill="#404040"
                />
                <clipPath id={clipPathIdRef.current} clipPathUnits="userSpaceOnUse">
                    <rect x="13" y={fillY} width="114" height={fillHeight} />
                </clipPath>
                <g clipPath={`url(#${clipPathIdRef.current})`}>
                    <path
                        d="M98.8,83.7V16.6c0-.5-.3-1-.8-1.3l-27.4-12.7c-.4-.2-.8-.2-1.1,0l-27.4,12.7c-.5.2-.8.7-.8,1.3v4.5h0v72.6c0,.5.2.9.6,1.1l18.3,10.8,1.4.9,1.2.8c.7.5,1.1,1.3,1.1,2.3v4.1c0,.2.1.4.3.4h11.6c.2,0,.3-.2.3-.4v-4.1c0-.9.4-1.8,1.1-2.3l1-.7h0s0,0,0,0h.2c0-.1,11.2-6.7,11.2-6.7l8.4-5c.4-.2.7-.7.7-1.1v-9.9c0,0,0-.1,0-.2Z"
                        fill="#FFD200"
                    />
                </g>
                {clampedPercent < 100 && (
                    <path
                        d="M98.8,29.4v-12.8c0-.5-.3-1-.8-1.3l-27.4-12.7c-.4-.2-.8-.2-1.1,0l-27.4,12.7c-.5.2-.8.7-.8,1.3v4.5h0v8.2h57.6Z"
                        fill="#fff"
                    />
                )}
                <text x="70" y="75" textAnchor="middle" fontWeight="bold" fontSize="18" fill="#404040" fontStyle="normal">
                    {Number.isFinite(percent) && percent >= 0
                        ? `${Number.isInteger(clampedPercent) ? clampedPercent : Number(clampedPercent.toFixed(2))}%`
                        : '?'}
                </text>
                {warning && (
                    <g ref={warnGroupRef}>
                        <circle cx="125" cy="15" r="11" fill="#fff" stroke="#404040" strokeWidth="2" />
                        <text x="125" y="21" textAnchor="middle" fontWeight="bold" fontSize="18" fill="#404040" fontStyle="normal">
                            !
                        </text>
                    </g>
                )}
            </svg>
            {warning && overlayRect && (
                <div
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onFocus={() => setShowTooltip(true)}
                    onBlur={() => setShowTooltip(false)}
                    tabIndex={0}
                    role="button"
                    aria-label={warningMessage || 'Warnung'}
                    style={{
                        position: 'absolute',
                        left: overlayRect.left,
                        top: overlayRect.top,
                        width: overlayRect.width,
                        height: overlayRect.height,
                        cursor: 'pointer',
                        background: 'rgba(0,0,0,0.001)',
                        zIndex: 2,
                    }}
                />
            )}
            {warning && showTooltip && warningMessage && (
                <div
                    style={{
                        position: 'absolute',
                        top: -40,
                        right: 0,
                        background: '#fff',
                        color: 'red',
                        border: '1px solid red',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        transform: 'translate(130px, 40px)',
                    }}
                >
                    {warningMessage}
                </div>
            )}
        </div>
    );
};

export default SiloIcon;
